import React, { useEffect, useState, useCallback } from 'react';
import {
  blockedAddressesApi,
  BlockedAddress,
  BlockedAddressInput,
} from '../services/api';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  ShieldExclamationIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { COUNTRIES } from '../utils/addressData';

const EMPTY_FORM: BlockedAddressInput = {
  label: '',
  name: '',
  email: '',
  phone: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  zip: '',
  country: 'US',
  reason: '',
  notes: '',
  active: true,
};

export const BlockedAddresses: React.FC = () => {
  const [items, setItems] = useState<BlockedAddress[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showActive, setShowActive] = useState<'all' | 'true' | 'false'>('true');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<BlockedAddress | null>(null);
  const [form, setForm] = useState<BlockedAddressInput>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [detailItem, setDetailItem] = useState<BlockedAddress | null>(null);
  const [detailMatches, setDetailMatches] = useState<any[]>([]);
  const [retroToast, setRetroToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (search) params.search = search;
      if (showActive !== 'all') params.active = showActive;
      const result = await blockedAddressesApi.list(params);
      setItems(result.blocked || []);
    } catch (err) {
      console.error('Failed to load blocked addresses:', err);
    } finally {
      setLoading(false);
    }
  }, [search, showActive]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (item: BlockedAddress) => {
    setEditing(item);
    setForm({
      label: item.label || '',
      name: item.name || '',
      email: item.email || '',
      phone: item.phone || '',
      address_line1: item.address_line1 || '',
      address_line2: item.address_line2 || '',
      city: item.city || '',
      state: item.state || '',
      zip: item.zip || '',
      country: item.country || 'US',
      reason: item.reason || '',
      notes: item.notes || '',
      active: item.active,
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.address_line1 && !form.email && !form.phone && !form.name) {
      alert('Provide at least one of: address, email, phone, or name.');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await blockedAddressesApi.update(editing.id, form);
      } else {
        const result = await blockedAddressesApi.create(form);
        if (result.retroactively_flagged > 0) {
          setRetroToast(
            `Added. ${result.retroactively_flagged} existing order(s) were retroactively flagged.`
          );
          setTimeout(() => setRetroToast(null), 6000);
        }
      }
      setShowForm(false);
      load();
    } catch (err: any) {
      alert(err.response?.data?.error || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item: BlockedAddress) => {
    if (!window.confirm(`Remove "${item.label || item.address_line1 || item.email}" from blocklist?`)) return;
    try {
      await blockedAddressesApi.remove(item.id);
      load();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to remove');
    }
  };

  const toggleActive = async (item: BlockedAddress) => {
    try {
      await blockedAddressesApi.update(item.id, { active: !item.active });
      load();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update');
    }
  };

  const openDetail = async (item: BlockedAddress) => {
    setDetailItem(item);
    setDetailMatches([]);
    try {
      const result = await blockedAddressesApi.get(item.id);
      setDetailMatches(result.matches || []);
    } catch (err) {
      console.error(err);
    }
  };

  const formatAddress = (item: BlockedAddress) => {
    const parts = [item.address_line1, item.address_line2, item.city, item.state, item.zip]
      .filter(Boolean)
      .join(', ');
    return parts || '—';
  };

  return (
    <div className="p-4 lg:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 hidden lg:flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-1 flex items-center gap-3">
              <ShieldExclamationIcon className="w-8 h-8 text-red-600" />
              Blocked Addresses
            </h1>
            <p className="text-slate-600">
              New orders are automatically checked against this list. Matches are flagged so they can't be shipped without manual review.
            </p>
          </div>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
          >
            <PlusIcon className="w-5 h-5" />
            Add Block
          </button>
        </div>

        {retroToast && (
          <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 flex items-start gap-2">
            <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <span>{retroToast}</span>
          </div>
        )}

        {/* Search / filter bar */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-subtle p-3 lg:p-4 mb-4 sticky top-0 z-20">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Search by name, email, address, city..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm"
              />
              <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            </div>
            <select
              value={showActive}
              onChange={(e) => setShowActive(e.target.value as any)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
            >
              <option value="true">Active only</option>
              <option value="false">Disabled only</option>
              <option value="all">All</option>
            </select>
            <button
              onClick={openCreate}
              className="lg:hidden flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium text-sm"
            >
              <PlusIcon className="w-5 h-5" />
              Add Block
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-subtle overflow-hidden">
          <div className="overflow-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Block</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Match Fields</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Reason</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Hits</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Last Match</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {loading && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">Loading...</td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center">
                    <ShieldExclamationIcon className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <h3 className="text-lg font-medium text-slate-900 mb-1">No blocked addresses</h3>
                    <p className="text-slate-500 text-sm mb-4">Add the first one to start auto-flagging suspicious orders.</p>
                    <button onClick={openCreate} className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium">
                      <PlusIcon className="w-4 h-4" />
                      Add Block
                    </button>
                  </td></tr>
                )}
                {!loading && items.map(item => (
                  <tr
                    key={item.id}
                    className={`hover:bg-slate-50 cursor-pointer ${!item.active ? 'opacity-60' : ''}`}
                    onClick={() => openDetail(item)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {!item.active && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">
                            Disabled
                          </span>
                        )}
                        <div>
                          <div className="text-sm font-medium text-slate-900">
                            {item.label || item.name || item.email || item.address_line1 || 'Unnamed'}
                          </div>
                          {item.label && (item.name || item.email) && (
                            <div className="text-xs text-slate-500">{item.name || item.email}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 max-w-md">
                      <div className="space-y-0.5">
                        {item.address_line1 && <div className="text-slate-700">📍 {formatAddress(item)}</div>}
                        {item.email && <div className="text-slate-600 text-xs">✉ {item.email}</div>}
                        {item.phone && <div className="text-slate-600 text-xs">☎ {item.phone}</div>}
                        {item.name && !item.label && <div className="text-slate-600 text-xs">👤 {item.name}</div>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 max-w-xs">
                      <div className="line-clamp-2">{item.reason || '—'}</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${item.match_count > 0 ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500'}`}>
                        {item.match_count || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {item.last_matched_at ? new Date(item.last_matched_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => toggleActive(item)}
                          className={`px-2 py-1 text-xs font-medium rounded ${item.active ? 'text-amber-600 hover:bg-amber-50' : 'text-emerald-600 hover:bg-emerald-50'}`}
                          title={item.active ? 'Disable' : 'Enable'}
                        >
                          {item.active ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          onClick={() => openEdit(item)}
                          className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded"
                          title="Edit"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => remove(item)}
                          className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add/Edit modal */}
      {showForm && (
        <BlockedFormModal
          editing={editing}
          form={form}
          setForm={setForm}
          saving={saving}
          onCancel={() => setShowForm(false)}
          onSave={save}
        />
      )}

      {/* Detail modal */}
      {detailItem && (
        <DetailModal item={detailItem} matches={detailMatches} onClose={() => { setDetailItem(null); setDetailMatches([]); }} />
      )}
    </div>
  );
};

/* -------------------------------------------------------------------------- */

const BlockedFormModal: React.FC<{
  editing: BlockedAddress | null;
  form: BlockedAddressInput;
  setForm: React.Dispatch<React.SetStateAction<BlockedAddressInput>>;
  saving: boolean;
  onCancel: () => void;
  onSave: () => void;
}> = ({ editing, form, setForm, saving, onCancel, onSave }) => {
  const set = (k: keyof BlockedAddressInput, v: any) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 z-10 flex items-center justify-between">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldExclamationIcon className="w-6 h-6 text-red-600" />
            {editing ? 'Edit Block' : 'New Block'}
          </h2>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-600">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="px-6 py-6 space-y-5">
          <div className="text-sm text-slate-600 bg-blue-50 border border-blue-200 rounded-lg p-3">
            Provide <strong>at least one</strong> of: address, email, phone, or name. New orders matching <strong>any</strong> field will be flagged.
          </div>

          <Field label="Label (optional)" hint="Friendly nickname e.g. 'Akron scammer'">
            <input className={inputClass} value={form.label || ''} onChange={(e) => set('label', e.target.value)} />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Name">
              <input className={inputClass} value={form.name || ''} onChange={(e) => set('name', e.target.value)} placeholder="Full name or business" />
            </Field>
            <Field label="Email">
              <input type="email" className={inputClass} value={form.email || ''} onChange={(e) => set('email', e.target.value)} />
            </Field>
            <Field label="Phone">
              <input className={inputClass} value={form.phone || ''} onChange={(e) => set('phone', e.target.value)} placeholder="+1 555 555 5555" />
            </Field>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Address (street + zip combined make the match)</h3>
            <div className="space-y-3">
              <Field label="Street Address">
                <input className={inputClass} value={form.address_line1 || ''} onChange={(e) => set('address_line1', e.target.value)} placeholder="21 E. Lowell Ave" />
              </Field>
              <Field label="Apt / Suite (optional)">
                <input className={inputClass} value={form.address_line2 || ''} onChange={(e) => set('address_line2', e.target.value)} />
              </Field>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Field label="City">
                  <input className={inputClass} value={form.city || ''} onChange={(e) => set('city', e.target.value)} />
                </Field>
                <Field label="State / Province">
                  <input className={inputClass} value={form.state || ''} onChange={(e) => set('state', e.target.value)} placeholder="OH" />
                </Field>
                <Field label="Zip / Postal Code">
                  <input className={inputClass} value={form.zip || ''} onChange={(e) => set('zip', e.target.value)} placeholder="44310" />
                </Field>
              </div>
              <Field label="Country">
                <select className={inputClass} value={form.country || 'US'} onChange={(e) => set('country', e.target.value)}>
                  {COUNTRIES.map(c => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
              </Field>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4 space-y-3">
            <Field label="Reason for blocking" hint="Shown on flagged orders so staff understand why">
              <input className={inputClass} value={form.reason || ''} onChange={(e) => set('reason', e.target.value)} placeholder="Repeated chargebacks; claimed item not received x3" />
            </Field>
            <Field label="Internal notes (optional)">
              <textarea className={`${inputClass} h-20`} value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} />
            </Field>
            {editing && (
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={!!form.active} onChange={(e) => set('active', e.target.checked)} />
                Active (uncheck to disable without deleting)
              </label>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex justify-end gap-2">
          <button onClick={onCancel} disabled={saving} className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg font-medium">
            Cancel
          </button>
          <button onClick={onSave} disabled={saving} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50">
            {saving ? 'Saving...' : editing ? 'Save Changes' : 'Add to Blocklist'}
          </button>
        </div>
      </div>
    </div>
  );
};

const DetailModal: React.FC<{
  item: BlockedAddress;
  matches: any[];
  onClose: () => void;
}> = ({ item, matches, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 z-10 flex items-center justify-between">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldExclamationIcon className="w-6 h-6 text-red-600" />
            {item.label || 'Blocked Entry'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>
        <div className="px-6 py-6 space-y-5">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><div className="text-xs text-slate-500">Name</div><div>{item.name || '—'}</div></div>
            <div><div className="text-xs text-slate-500">Email</div><div>{item.email || '—'}</div></div>
            <div><div className="text-xs text-slate-500">Phone</div><div>{item.phone || '—'}</div></div>
            <div><div className="text-xs text-slate-500">Status</div><div>{item.active ? 'Active' : 'Disabled'}</div></div>
            <div className="col-span-2"><div className="text-xs text-slate-500">Address</div><div>{[item.address_line1, item.address_line2, item.city, item.state, item.zip].filter(Boolean).join(', ') || '—'}</div></div>
            {item.reason && <div className="col-span-2"><div className="text-xs text-slate-500">Reason</div><div>{item.reason}</div></div>}
            {item.notes && <div className="col-span-2"><div className="text-xs text-slate-500">Notes</div><div className="whitespace-pre-wrap">{item.notes}</div></div>}
          </div>

          <div>
            <h3 className="text-sm font-semibold text-slate-900 mb-2">Matched Orders ({matches.length})</h3>
            {matches.length === 0 ? (
              <p className="text-sm text-slate-500">No orders have matched this rule yet.</p>
            ) : (
              <div className="space-y-2">
                {matches.map((m: any) => (
                  <div key={m.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg text-sm">
                    <div>
                      <div className="font-medium text-slate-900">Order #{m.order_number || '—'}</div>
                      <div className="text-xs text-slate-600">{m.customer_name} • {m.customer_email}</div>
                      <div className="text-xs text-slate-500">Matched on <span className="font-medium">{m.match_type}</span> — {new Date(m.created_at).toLocaleString()}</div>
                    </div>
                    {m.total_price && (
                      <div className="text-right text-sm font-medium text-slate-700">
                        ${parseFloat(m.total_price).toFixed(2)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
    {children}
    {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
  </div>
);

const inputClass =
  'w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent';

export default BlockedAddresses;
